"use client";

import Link from "next/link";
import { useCallback } from "react";
import { useTranslations } from "next-intl";
import { Bookmark, ClipboardList, LogOut, Settings, SlidersHorizontal, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CommerceIcons } from "@/lib/icons/commerce-icons";
import { ProfileStatsIcons } from "@/lib/icons/profile-stats-icons";
import { signOutAction } from "@/app/actions";
import { appendSecurityLogFormData } from "@/lib/security-log-client";

/**
 * Quick-access links for the header account menu — the most-used destinations,
 * pulled from the same `nav` i18n namespace as the full /account hub
 * (`components/account/account-menu-sections.tsx`) so labels stay in sync.
 * "My profile" targets /profile, which redirects to the viewer's own profile.
 */
const QUICK_LINKS = [
  { key: "myProfile", href: "/profile", icon: User },
  { key: "myListings", href: "/my-listings", icon: ProfileStatsIcons.listings },
  { key: "orderManagement", href: "/order-management", icon: ClipboardList },
  { key: "ordersPlaced", href: "/orders-placed", icon: CommerceIcons.buyNow },
  { key: "offers", href: "/offers", icon: CommerceIcons.offer },
  { key: "saved", href: "/saved", icon: Bookmark },
  { key: "settings", href: "/settings", icon: Settings },
] as const;

/**
 * HeaderAccountMenu — the header's user-icon dropdown. Replaces the previous
 * direct `<Link href="/account">`: the icon now opens a compact menu of the
 * most-used sections, an "Account" entry to the full hub (/account), and Sign
 * out. Used by `HeaderActions` (feed + global header) for logged-in users.
 */
export function HeaderAccountMenu({ triggerClassName }: { triggerClassName?: string }) {
  const tNav = useTranslations("nav");
  const tCommon = useTranslations("common");

  const handleSignOut = useCallback(async () => {
    const formData = new FormData();
    appendSecurityLogFormData(formData);
    await signOutAction(formData);
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={tNav("sectionAccount")} className={triggerClassName}>
          <User className="size-[21px]" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {QUICK_LINKS.map(({ key, href, icon: Icon }) => (
          <DropdownMenuItem key={key} asChild>
            <Link href={href}>
              <Icon className="mr-2 size-4 shrink-0" />
              {tNav(key)}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/account">
            <SlidersHorizontal className="mr-2 size-4 shrink-0" />
            {tNav("sectionAccount")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void handleSignOut()}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 size-4 shrink-0" />
          {tCommon("logOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
